// @flow

import * as React from 'react';
import styles from './CollapsibleContainer.module.scss';

type Props = {|
  children: React.Node,
  maxLines: number,
  id: string
|};

type State = {|
  shouldCollapse: boolean,
  isExpanded: boolean,
  maxHeight: number | string
|};

type Action =
  | {|
      type: 'SET_SHOULD_COLLAPSE',
      contentHeight: number,
      collapsedHeight: number
    |}
  | {|
      type: 'TOGGLE_EXPANDED',
      collapsedHeight: number
    |};

function collapseReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_SHOULD_COLLAPSE': {
      const { contentHeight, collapsedHeight } = action;
      return {
        ...state,
        shouldCollapse: contentHeight > collapsedHeight
      };
    }
    case 'TOGGLE_EXPANDED': {
      const isExpanded = !state.isExpanded;
      return {
        ...state,
        isExpanded,
        maxHeight: isExpanded ? 'unset' : action.collapsedHeight
      };
    }
    default:
      return state;
  }
}

// The constant below is a rough calculation of the height of a paragraph
//
// This value is used to calculate the initial collapsed height of the content,
// based on the maxLines prop (collapsedHeight = maxLines * HEIGHT_OF_PARAGRAPH).
//
// We want the component to reserve space for collapsed content *before*
// the browser has measured the true content height in order to avoid layout shifts.
//
// The risky part is that height of a paragraph can vary depending on font size, font family
// line height and similar styling
//
// Why is this compromise acceptable?
// We are currently going with this because it's fair to assume that more often than not,
// the content is in collapsed state, therefore `shouldCollapse` is true by default.
// So instead of checking if the the content should be collapsible, we are checking the
// opposite. Again, because it's more likely that it is.
//
// After mount, we finally have access to the real DOM
// and can check if the text exceeds the maxheight and we can update
// `shouldCollapse` if needed
//
// This means the initial estimate only needs to be "good enough" to prevent
// layout shifts — it does not need to be perfect in the long run.
//
// This is obviously not ideal and we should rethink this in the future
// Maybe we could pass down something like line height or other styles as
// props, as information that could make this calculcation less risky
//
// Until then, this approach is a pragmatic compromise between avoiding layout shift
// and keeping the implementation simple.
const HEIGHT_OF_PARAGRAPH = 26;

export default function CollapsibleContainer(props: Props): React.Node {
  const collapsedHeight = props.maxLines * HEIGHT_OF_PARAGRAPH;

  const [{ shouldCollapse, isExpanded, maxHeight }, dispatch] =
    React.useReducer(collapseReducer, {
      shouldCollapse: true,
      isExpanded: false,
      maxHeight: collapsedHeight
    });

  const textRef = React.useRef<HTMLElement | null>(null);

  React.useLayoutEffect(() => {
    function handleResize() {
      if (textRef.current) {
        dispatch({
          type: 'SET_SHOULD_COLLAPSE',
          contentHeight: textRef.current.scrollHeight,
          collapsedHeight
        });
      }
    }

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [collapsedHeight]);

  function handleClick() {
    dispatch({ type: 'TOGGLE_EXPANDED', collapsedHeight });
  }

  return (
    <>
      <div
        id={props.id}
        className={styles.container}
        style={{ maxHeight }}
        data-testid="collapsible-container"
      >
        <div ref={textRef}>{props.children}</div>

        {shouldCollapse && !isExpanded && (
          <div className={styles.gradientOverlay}>
            <button
              onClick={handleClick}
              className={styles.inlineButton}
              aria-expanded={isExpanded}
              aria-controls={props.id}
            >
              {I18n.t('venues.show.collapsible_container.show_more')}
            </button>
          </div>
        )}
      </div>

      {shouldCollapse && isExpanded && (
        <div className={styles.inlineButtonWrapper}>
          <button
            onClick={handleClick}
            className={styles.inlineButton}
            aria-expanded={isExpanded}
            aria-controls={props.id}
          >
            {I18n.t('venues.show.collapsible_container.show_less')}
          </button>
        </div>
      )}
    </>
  );
}
